import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ReactLenis, useLenis } from "lenis/react";
import { Toaster } from "sonner";
import { isAutoAccountHost, isMerchantHost, isProviderHost, isAutoHost, isCustomDomain } from "@/lib/platformHosts";
import "@/App.css";

import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Preview from "@/pages/Preview";
import Onboard from "@/pages/Onboard";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import Kitchen from "@/pages/Kitchen";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Account from "@/pages/Account";
import RequireAuth from "@/components/RequireAuth";
import CheckoutSuccessSpec from "@/storefront/CheckoutSuccessSpec";
import Build from "@/pages/Build";
import Solutions from "@/pages/Solutions";
import Privacy from "@/pages/legal/Privacy";
import Terms from "@/pages/legal/Terms";
import Cookies from "@/pages/legal/Cookies";
import Refund from "@/pages/legal/Refund";
import FAQ from "@/pages/FAQ";
import Partners from "@/pages/Partners";
import Waitlist from "@/pages/Waitlist";
import Subscribe from "@/pages/Subscribe";
import SubscribeSuccess from "@/pages/SubscribeSuccess";
import SubscribeClaim from "@/pages/SubscribeClaim";
import SeoManager from "@/components/SeoManager";

const Storefront = lazy(() => import("@/pages/Storefront"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const PhoneSim = lazy(() => import("@/pages/PhoneSim"));
const SpecStorefrontDemo = lazy(() => import("@/storefront/SpecStorefrontDemo"));
const StorefrontBySlug = lazy(() => import("@/storefront/StorefrontBySlug"));
const DomainStorefront = lazy(() => import("@/storefront/DomainStorefront"));
const Demo = lazy(() => import("@/pages/Demo"));
const Gallery = lazy(() => import("@/pages/Gallery"));
const AutoRepair = lazy(() => import("@/pages/AutoRepair"));
const DocsIndex = lazy(() => import("@/pages/docs"));
const MerchantGuide = lazy(() => import("@/pages/docs/MerchantGuide"));
const ServiceProviderGuide = lazy(() => import("@/pages/docs/ServiceProviderGuide"));
const AiHostGuide = lazy(() => import("@/pages/docs/AiHostGuide"));

// On a merchant's custom domain (e.g. orders.joesbakery.com) every path
// should render DomainStorefront — it resolves the hostname → slug then
// renders the spec. On the platform domain (ordrs.ai / localhost) normal
// routing applies. Host detection lives in lib/platformHosts.js so other
// pages (e.g. Subscribe) can read the same signal.
function rootRouteForHost() {
  if (isMerchantHost) return "/merchant";
  if (isProviderHost) return "/provider";
  if (isAutoAccountHost) return "/autoaccount";
  return null;
}

// The Auto hostname is a dedicated vertical surface. Keep generic merchant,
// provider, storefront, and builder routes off that host until they have an
// auto-specific public workflow instead of showing the wrong product.
function isAllowedAutoHostPath(pathname) {
  return pathname === "/"
    || pathname === "/auto"
    || pathname === "/auto-repair"
    || pathname === "/contact"
    || pathname === "/privacy"
    || pathname === "/terms"
    || pathname === "/cookies"
    || pathname === "/login"
    || pathname === "/signup"
    || pathname === "/forgot-password"
    || pathname === "/reset-password"
    || pathname === "/autoaccount"
    || pathname.startsWith("/dashboard/")
    || pathname.startsWith("/fulfillment/")
    || pathname.startsWith("/kitchen/")
    || pathname.startsWith("/phone-sim/");
}

function ScrollToTop() {
  const { pathname, search, hash } = useLocation();
  const lenis = useLenis();

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    if (hash) return;
    requestAnimationFrame(() => {
      lenis?.scrollTo(0, { immediate: true, force: true });
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    });
  }, [pathname, search, hash, lenis]);

  return null;
}

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center p-12">
      <p className="text-muted-foreground animate-pulse">Loading…</p>
    </div>
  );
}

export default function App() {
  if (isCustomDomain) {
    return (
      <ReactLenis root options={{ lerp: 0.085, wheelMultiplier: 0.9, touchMultiplier: 1.2, anchors: { offset: -72 } }}>
        <div className="App">
          <Toaster position="top-right" richColors />
          <Suspense fallback={<RouteFallback />}>
            <DomainStorefront />
          </Suspense>
        </div>
      </ReactLenis>
    );
  }

  return (
    <ReactLenis root options={{ lerp: 0.085, wheelMultiplier: 0.9, touchMultiplier: 1.2, anchors: { offset: -72 } }}>
      <div className="App">
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <SeoManager />
          <ScrollToTop />
          {isAutoHost && !isAllowedAutoHostPath(window.location.pathname) ? (
            <Navigate to="/" replace />
          ) : (
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={isAutoHost ? <AutoRepair /> : rootRouteForHost() ? <Navigate to={rootRouteForHost()} replace /> : <Landing />} />
                <Route path="/spec-demo" element={<SpecStorefrontDemo />} />
                <Route path="/demo" element={<Demo />} />
                <Route path="/gallery" element={<Gallery />} />
                <Route path="/s/:slug/checkout/success" element={<CheckoutSuccessSpec />} />
                <Route path="/s/:slug/*" element={<StorefrontBySlug />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/solutions" element={<Solutions />} />
                <Route path="/auto" element={<AutoRepair />} />
                <Route path="/auto-repair" element={<AutoRepair />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/cookies" element={<Cookies />} />
                <Route path="/refund" element={<Refund />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/waitlist" element={<Waitlist />} />
                <Route path="/docs" element={<DocsIndex />} />
                <Route path="/docs/merchants" element={<MerchantGuide />} />
                <Route path="/docs/service-providers" element={<ServiceProviderGuide />} />
                <Route path="/docs/ai-host" element={<AiHostGuide />} />
                <Route path="/subscribe" element={<Subscribe />} />
                <Route path="/subscribe/success" element={<SubscribeSuccess />} />
                <Route path="/subscribe/claim" element={<SubscribeClaim />} />
                <Route path="/partners" element={<Partners />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/merchant" element={<RequireAuth><Account preferredCategory="merchant" /></RequireAuth>} />
                <Route path="/provider" element={<RequireAuth><Account preferredCategory="service_provider" /></RequireAuth>} />
                <Route path="/autoaccount" element={<RequireAuth><Account preferredVertical="auto" /></RequireAuth>} />
                <Route path="/account" element={<RequireAuth><Account /></RequireAuth>} />
                <Route path="/preview/:slug" element={<Preview />} />
                <Route path="/build/:slug" element={<Build />} />
                <Route path="/onboard/:slug" element={<Onboard />} />
                <Route path="/t/:slug" element={<Storefront />} />
                <Route path="/t/:slug/checkout/success" element={<CheckoutSuccess />} />
                <Route path="/dashboard/:slug" element={<RequireAuth><Dashboard /></RequireAuth>} />
                <Route path="/kitchen/:slug" element={<RequireAuth><Kitchen /></RequireAuth>} />
                <Route path="/fulfillment/:slug" element={<RequireAuth><Kitchen /></RequireAuth>} />
                <Route path="/phone-sim/:slug" element={<PhoneSim />} />
              </Routes>
            </Suspense>
          )}
        </BrowserRouter>
      </div>
    </ReactLenis>
  );
}
